import React, { useState } from 'react';
import { ButtonField, Icon } from '@wk/components-v3-react16';
import XMLViewer from 'react-xml-viewer';
import { useExportWorkflow } from './useExportWorkflow';
import styles from './XMLCodeTab.module.scss';

const xmlTheme = {
  attributeKeyColor: '#007ac3',
  attributeValueColor: '#008000',
  cdataColor: '#1D781D',
  commentColor: '#aaa',
  fontFamily: 'monospace',
  separatorColor: '#333',
  tagColor: 'rgb(31, 35, 40)',
  textColor: '#333',
};

const XMLCodeTab: React.FC = () => {
  const { xmlContent, isLoading, isError, error, refetch, isFetching } =
    useExportWorkflow();
  const [copySuccess, setCopySuccess] = useState(false);
  const [showLines, setShowLines] = useState(true);
  const toggleLines = () => setShowLines(!showLines);
  const handleCopyToClipboard = async () => {
    if (!xmlContent) {
      return;
    }

    try {
      await navigator.clipboard.writeText(xmlContent);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1000);
    } catch (caught) {
      console.error('Failed to copy XML to clipboard:', caught);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>Generating XML...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <div className={styles.errorMessage}>
            Error generating XML: {error?.message || 'Unknown error'}
          </div>
          <button onClick={handleRefresh}>Retry</button>
        </div>
      </div>
    );
  }

  if (!xmlContent) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>No XML content available</div>
      </div>
    );
  }
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>XML Code</h2>
        <div className={styles.actions}>
          <ButtonField type="primary">
            <button onClick={handleCopyToClipboard}>
              <Icon name={copySuccess ? 'wk-icon-check' : 'wk-icon-files'} />
            </button>
          </ButtonField>
          <ButtonField type="primary">
            <button onClick={toggleLines}>
              <Icon name="wk-icon-list-bullet-number" />
            </button>
          </ButtonField>
          <ButtonField type="primary">
            <button onClick={handleRefresh}>
              <Icon name="wk-icon-sync" isSpin={isFetching} />
            </button>
          </ButtonField>
        </div>
      </div>
      <div className={styles.xmlViewerContainer}>
        <div className={styles.xmlViewerWrapper}>
          <XMLViewer
            xml={xmlContent}
            theme={xmlTheme}
            showLineNumbers={showLines}
          />
        </div>
      </div>
    </div>
  );
};

export default XMLCodeTab;
